import { Author, Post } from 'codegen/schema'
import { AuthorDetails } from 'components/author-details'
import { Layout } from 'components/layout'
import { indexQuery } from 'lib/queries'
import { urlForImage } from 'lib/sanity'
import { getClient, overlayDrafts } from 'lib/sanity.server'
import Image from 'next/image'
import { withAuthUser } from 'next-firebase-auth'
import styled from 'styled-components'
import { urlWithSiteOrigin } from 'utils/url-with-site-origin'

const CardWrapper = styled.div`
  max-width: 75rem;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  justify-content: center;
  grid-gap: 1rem;
  padding: 1rem;
  background-color: ${({ theme }) => theme.palette.background.paper};

  @media (max-width: 64rem) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 43.75rem) {
    grid-template-columns: 1fr;
  }
`

const Header = styled.h1`
  max-width: 75rem;
  margin: 0 auto;
  padding: 1rem;
`

const Card = styled.div`
  background-color: ${({ theme }) => theme.palette.background.default};
  border-radius: 2px;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.spacing(0.5)};

  @media (max-width: 43.75rem) {
    border-radius: ${({ theme }) => theme.spacing(1)};
  }
`

const CardImageWrapper = styled.div`
  position: relative;
  height: 240px;

  & img {
    height: 100%;
    width: 100%;
    object-fit: cover;
  }
`

const CardBottom = styled.div`
  padding: 1rem;
`

const CardTag = styled.span`
  display: inline-block;
  color: ${({ theme }) => theme.palette.primary.main};
  border: 1px solid ${({ theme }) => theme.palette.primary.main};
  border-radius: 2px;
  padding: 0 0.5rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  height: 1.625rem;

  &:not(:last-child) {
    margin-right: 0.5rem;
  }
`

const CardTitle = styled.a`
  display: block;
  text-decoration: none;
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: 1.125rem;
  font-weight: 600;
  height: 3.375rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  &:hover {
    text-decoration: underline;
  }
`

interface IndexProps {
  preview?: boolean
  allPosts?: Post[]
}

function Index({ allPosts, preview }: IndexProps) {
  return (
    <>
      <Layout
        seoProps={{
          title: 'Read interesting articles about productivity and improvement',
          description:
            'Read the articles that will provide you with various information related to touch typing and productivity improvement',
          canonicalUrl: urlWithSiteOrigin('/articles/'),
          pageUrl: urlWithSiteOrigin('/articles/'),
        }}
      >
        <Header>Articles</Header>
        <CardWrapper>
          {allPosts &&
            allPosts?.length > 0 &&
            allPosts.map((post: Post, postIndex) => {
              // TODO: n: the image was rendered using this https://www.sanity.io/docs/image-url but it was not working
              // therefore, for now the src was read directly from sanity data and used it like https://www.sanity.io/docs/image-url#custom-cdn-domains
              // please verify what is the best approach to render images
              const srcUrl = post?.heroImage?.image?.asset._ref
              const alt = post?.heroImage?.alt
              const title = post?.title
              if (!srcUrl) return null
              const src = urlForImage(srcUrl).height(260).width(400).fit('crop').url() || ''

              return (
                <Card key={post.title} data-cy="articles-card">
                  {preview && (
                    <div>
                      <b>Preview mode!</b>
                    </div>
                  )}

                  <CardImageWrapper>
                    {post.heroImage && (
                      <Image
                        // to improve LCP performance, we only load the first 12 images asap
                        priority={postIndex < 12}
                        src={src}
                        width={400}
                        height={260}
                        alt={alt || ''}
                        title={title}
                      />
                    )}
                  </CardImageWrapper>

                  <CardBottom>
                    <div>
                      {post.tags?.map((tag, index) => {
                        return <CardTag key={index}>{tag.label}</CardTag>
                      })}
                    </div>

                    <CardTitle title={`Go to ${post.title} page`} href={`/articles/${post.slug}/`}>
                      {post.title}
                    </CardTitle>

                    <AuthorDetails
                      author={post?.author as Author | undefined}
                      publishedDate={post && post?.publishedAt}
                      forArticleCard
                    />
                  </CardBottom>
                </Card>
              )
            })}
        </CardWrapper>
      </Layout>
    </>
  )
}

export async function getStaticProps({ preview = false }) {
  const allPosts = overlayDrafts(await getClient(preview).fetch(indexQuery))
  return {
    props: { allPosts, preview },
  }
}

export default withAuthUser<IndexProps>()(Index)
